import React, { useState, useEffect } from 'react';
import { Heart, PenLine, X, Send, Check } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * WishActionBar Component - Split Action Bar for Guest Wishes
 * 
 * Phase 1: Contextual Entry - Horizontal split bar under guest's meal preference
 * - Left: "Send Love" (outlined) - Quick heart tap
 * - Right: "Leave a Wish" (solid) - Opens bottom sheet for message
 * 
 * Phase 2: Interactions
 * - Booster Path: Heart animation → Success screen
 * - Poet Path: Bottom sheet → 400 char message → Success screen
 * 
 * Phase 3: Success Screen
 * - "Love Received" / "Your love has reached them."
 * - Auto-dismiss after 2 seconds
 * - Returns checkmark state to parent
 */
export function WishActionBar({ 
  slug, 
  guestName, 
  guestId,
  tableName,
  colors,
  language = 'en',
  onWishSent
}) {
  const [showMessageSheet, setShowMessageSheet] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [heartSent, setHeartSent] = useState(false); // One-time heart tracking
  const [animateHeart, setAnimateHeart] = useState(false);

  // Check if test mode is enabled via URL parameter (?test=true)
  const isTestMode = new URLSearchParams(window.location.search).get('test') === 'true';

  // Check if heart was already sent (one-time only)
  // In test mode, allow unlimited hearts
  // Messages can be sent multiple times - no tracking needed
  useEffect(() => {
    if (isTestMode) return; // Skip check in test mode
    
    if (guestName && slug) {
      const guestKey = guestName.replace(/\s+/g, '_').toLowerCase();
      const heartKey = `wish_heart_${slug}_${guestKey}`;
      if (localStorage.getItem(heartKey)) {
        setHeartSent(true);
      }
    }
  }, [guestName, slug, isTestMode]);

  // Translations
  const translations = {
    en: {
      sendLove: 'Send Love',
      leaveWish: 'Leave a Wish',
      writeMessage: 'Write a little love for the couple...',
      send: 'Send Wish',
      cancel: 'Cancel',
      loveReceived: 'Love Received',
      loveReachedThem: 'Your love has reached them.',
      wishSent: 'Wish Sent'
    },
    pt: {
      sendLove: 'Enviar Amor',
      leaveWish: 'Deixar Desejo',
      writeMessage: 'Escreva um carinho para o casal...',
      send: 'Enviar Desejo',
      cancel: 'Cancelar',
      loveReceived: 'Amor Recebido',
      loveReachedThem: 'Seu amor chegou até eles.',
      wishSent: 'Desejo Enviado'
    },
    fr: {
      sendLove: 'Envoyer Amour',
      leaveWish: 'Laisser un Voeu',
      writeMessage: 'Écrivez un petit mot pour le couple...',
      send: 'Envoyer',
      cancel: 'Annuler',
      loveReceived: 'Amour Reçu',
      loveReachedThem: 'Votre amour leur est parvenu.',
      wishSent: 'Voeu Envoyé'
    },
    es: {
      sendLove: 'Enviar Amor',
      leaveWish: 'Dejar Deseo',
      writeMessage: 'Escribe un cariño para la pareja...',
      send: 'Enviar Deseo',
      cancel: 'Cancelar',
      loveReceived: 'Amor Recibido',
      loveReachedThem: 'Tu amor les ha llegado.',
      wishSent: 'Deseo Enviado'
    },
    ru: {
      sendLove: 'Отправить Любовь',
      leaveWish: 'Оставить Пожелание',
      writeMessage: 'Напишите пожелание для пары...',
      send: 'Отправить',
      cancel: 'Отмена',
      loveReceived: 'Любовь Получена',
      loveReachedThem: 'Ваша любовь дошла до них.',
      wishSent: 'Пожелание Отправлено'
    },
    it: {
      sendLove: 'Invia Amore',
      leaveWish: 'Lascia un Augurio',
      writeMessage: 'Scrivi un pensiero per la coppia...',
      send: 'Invia Augurio',
      cancel: 'Annulla',
      loveReceived: 'Amore Ricevuto',
      loveReachedThem: 'Il tuo amore è arrivato a loro.',
      wishSent: 'Augurio Inviato'
    }
  };

  const t = translations[language] || translations.en;

  const submitWish = async (wishType, wishMessage = null) => {
    setSending(true);
    try {
      await axios.post(`${API}/public/event/${slug}/wishes`, {
        guest_id: guestId,
        guest_name: guestName,
        table_name: tableName,
        wish_type: wishType,
        message: wishMessage
      });
      
      setShowMessageSheet(false);
      setMessage('');
      setShowSuccess(true);
      
      // Save to localStorage (one-time tracking for hearts only)
      // In test mode, don't save to localStorage - allow unlimited hearts
      // Messages can be sent multiple times
      if (!isTestMode && guestName && slug && wishType === 'heart') {
        const guestKey = guestName.replace(/\s+/g, '_').toLowerCase();
        localStorage.setItem(`wish_heart_${slug}_${guestKey}`, 'true');
        setHeartSent(true);
      }
      
      // Trigger haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      
      // Auto-dismiss success screen after 3 seconds
      setTimeout(() => {
        setShowSuccess(false);
        onWishSent?.();
      }, 3000);
      
    } catch (error) {
      console.error('Failed to send wish:', error);
      toast.error('Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleSendLove = () => {
    if (heartSent) return; // One-time only
    setAnimateHeart(true);
    setTimeout(() => setAnimateHeart(false), 600);
    submitWish('heart');
  };

  const handleSendMessage = () => {
    if (!message.trim()) return;
    submitWish('message', message.trim());
  };

  // Character counter - only show when nearing limit
  const showCharCounter = message.length > 320;
  const charLimit = 400;

  // Success Screen (Phase 3) - smoother animation, 4 second display
  if (showSuccess) {
    return (
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-in fade-in duration-500"
        style={{ backgroundColor: `${colors.background}f8` }}
      >
        <div className="text-center animate-in fade-in zoom-in duration-500 ease-out">
          {/* Heart Icon - single big heart */}
          <div className="mb-6">
            <div 
              className="w-20 h-20 mx-auto rounded-full flex items-center justify-center animate-pulse"
              style={{ backgroundColor: `${colors.primary}20` }}
            >
              <Heart 
                className="w-10 h-10 fill-current" 
                style={{ color: colors.primary }} 
              />
            </div>
          </div>
          
          <h2 
            className="text-2xl font-semibold mb-2" 
            style={{ color: colors.text }}
          >
            {t.loveReceived}
          </h2>
          <p 
            className="text-sm" 
            style={{ color: colors.textSecondary || colors.textMuted }}
          >
            {t.loveReachedThem}
          </p>
        </div>
      </div>
    );
  }

  // Message Bottom Sheet (Phase 2 - Poet Path)
  if (showMessageSheet) {
    return (
      <div 
        className="fixed inset-0 z-50 flex items-end justify-center"
        onClick={() => setShowMessageSheet(false)}
      >
        <div 
          className="absolute inset-0 animate-in fade-in duration-300"
          style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
        />
        <div 
          className="relative w-full max-w-lg rounded-t-xl p-6 animate-in slide-in-from-bottom duration-500 ease-out"
          onClick={(e) => e.stopPropagation()}
          style={{ backgroundColor: colors.cardBackground || '#fff' }}
          data-testid="wish-message-sheet"
        >
          {/* Handle bar */}
          <div className="w-12 h-1 mx-auto mb-4 rounded-full" style={{ backgroundColor: colors.border }} />
          
          {/* Close button */}
          <button 
            onClick={() => setShowMessageSheet(false)}
            className="absolute top-4 right-4 p-2 rounded-full transition-colors"
            style={{ color: colors.textMuted }}
          >
            <X size={20} />
          </button>
          
          {/* Message textarea */}
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, charLimit))}
            placeholder={t.writeMessage}
            className="w-full h-36 p-4 resize-none mb-4 text-base"
            style={{ 
              backgroundColor: colors.inputBackground || `${colors.border}30`,
              color: colors.text,
              border: `1px solid #ffffff`,
              borderRadius: '12px',
              outline: 'none'
            }}
            maxLength={charLimit}
            autoFocus
            data-testid="wish-message-input"
          />
          
          {/* Character count - only show when nearing limit */}
          {showCharCounter && (
            <div 
              className="text-right text-xs mb-4 transition-opacity"
              style={{ color: message.length > 380 ? '#ef4444' : colors.textMuted }}
            >
              {message.length}/{charLimit}
            </div>
          )}
          
          {/* Action buttons */}
          <div className="flex gap-3">
            <Button
              onClick={() => setShowMessageSheet(false)}
              variant="outline"
              className="flex-1 py-3 rounded-xl font-medium"
              style={{ 
                borderColor: colors.border, 
                color: colors.textMuted,
                backgroundColor: 'transparent'
              }}
            >
              {t.cancel}
            </Button>
            <Button
              onClick={handleSendMessage}
              disabled={!message.trim() || sending}
              className="flex-1 py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-all"
              style={{ 
                backgroundColor: message.trim() ? colors.primary : colors.border,
                color: message.trim() ? '#fff' : colors.textMuted
              }}
              data-testid="wish-send-message-btn"
            >
              {sending ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Send size={16} />
                  {t.send}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Phase 1: Split Action Bar
  return (
    <div className="flex gap-2 mt-3" data-testid="wish-action-bar">
      {/* Left: Send Love (outlined) - Booster Path - ONE TIME ONLY */}
      <button
        onClick={handleSendLove}
        disabled={sending || heartSent}
        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
          heartSent ? 'opacity-60 cursor-default' : 'hover:scale-[1.02] active:scale-[0.98]'
        }`}
        style={{ 
          backgroundColor: heartSent ? `${colors.primary}15` : 'transparent',
          border: `1.5px solid ${heartSent ? `${colors.primary}50` : colors.primary}`,
          color: colors.primary
        }}
        data-testid="wish-send-love-btn"
      >
        <Heart 
          className={`w-4 h-4 transition-all ${heartSent ? 'fill-current' : ''} ${animateHeart ? 'fill-current scale-125' : ''}`} 
        />
        <span className="text-sm">{heartSent ? (language === 'pt' ? 'Amor Enviado' : 'Love Sent') : t.sendLove}</span>
      </button>
      
      {/* Right: Leave a Wish (solid) - Poet Path - Can send multiple messages */}
      <button
        onClick={() => setShowMessageSheet(true)}
        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all hover:scale-[1.02] active:scale-[0.98]"
        style={{ 
          backgroundColor: colors.primary,
          color: '#fff'
        }}
        data-testid="wish-leave-wish-btn"
      >
        <PenLine className="w-4 h-4" />
        <span className="text-sm">{t.leaveWish}</span>
      </button>
    </div>
  );
}

export default WishActionBar;
